import { createAsyncThunk } from '@reduxjs/toolkit';
import { accountsStoreKey } from './accounts.const';
import axios, { AxiosResponse } from 'axios';
import { apiEndpoints } from '../../constants/api';
import { AccountOverview, OperationalCost } from '../../types';
import {
  AccountAndProjectList,
  FetchAccountAndProjectListParams,
  GetAccountsData,
  saveLinkPayloadType,
} from './accounts.types';
import { accountsSlice } from './accounts.slice';
import { notificationAlert } from '../notifications';
import { errorAbortHandler, formatErrorMessage } from '../../utils';

export const fetchAccounts = createAsyncThunk(
  `${accountsStoreKey}/fetchAccounts`,
  async (
    { fetchAllMembers, departmentFilters, periodFiltering }: GetAccountsData,
    thunkAPI,
  ) => {
    try {
      const response = await axios.post(apiEndpoints.fetchAccounts(), {
        ...departmentFilters,
        periodFiltering,
        ...{ fetchAllMembers },
      });

      return response.data.accounts.map((acc: AccountOverview) => {
        acc.grossMarginTargets = response.data.grossMarginTargets
          ? { ...response.data.grossMarginTargets }
          : undefined;
        acc.grossMarginHighlights = Array.isArray(
          response.data.grossMarginHighlights,
        )
          ? [...response.data.grossMarginHighlights]
          : undefined;

        acc.netMarginHighlights = Array.isArray(
          response.data.netMarginHighlights,
        )
          ? [...response.data.netMarginHighlights]
          : undefined;
        acc.portfolioMargins = response.data.financeStats
          ? { ...response.data.financeStats }
          : undefined;
        acc.operationalCost = response.data.operationalCost;
        return acc;
      });
    } catch (err) {
      thunkAPI.dispatch(
        accountsSlice.actions.cleanAccountsTableRow({
          errMessage: 'No Accounts found on this day :(',
        }),
      );
      throw new Error(String(err));
    }
  },
);

export const saveLinks = createAsyncThunk(
  `${accountsStoreKey}/saveLinks`,
  async (body: saveLinkPayloadType, thunkAPI) => {
    try {
      const response = await axios.post(`${apiEndpoints.saveLink()}`, body);
      thunkAPI.dispatch(notificationAlert('Links saved.'));
      return response.data;
    } catch (err) {
      thunkAPI.dispatch(
        notificationAlert(formatErrorMessage(err), { variant: 'error' }),
      );
      throw new Error(String(err));
    }
  },
);

export const saveOperationalCost = createAsyncThunk(
  `${accountsStoreKey}/operationalCost`,
  async (operationalCost: OperationalCost, thunkAPI) => {
    return await errorAbortHandler(
      async () => {
        const endpoint = apiEndpoints.saveOperationalCost();
        const response = await axios.post(endpoint, operationalCost);
        thunkAPI.dispatch(notificationAlert('Operational cost saved.'));
        return response.data as OperationalCost;
      },
      {
        signal: thunkAPI.signal,
        dispatch: thunkAPI.dispatch,
      },
    );
  },
);

export const getAccountAndProjectList = createAsyncThunk(
  `${accountsStoreKey}/getAccountAndProjectList`,
  async ({ showError }: FetchAccountAndProjectListParams, thunkAPI) => {
    try {
      const response: AxiosResponse<AccountAndProjectList> = await axios.get(
        apiEndpoints.getAccountAndProjectList(),
      );

      return response.data;
    } catch (err) {
      if (showError) {
        thunkAPI.dispatch(
          notificationAlert(formatErrorMessage(err), { variant: 'error' }),
        );
      }
      throw new Error(String(err));
    }
  },
);
